import { useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export type RepairNextActionProps = {
  status: string;
  actorName: string;
  picName: string;
  technicianName: string;
  canSetPrice: boolean;
  canDecide: boolean;
  canBypass: boolean;
  guideCost?: string | null;
  confirmedPrice?: string | null;
  proposalVersion: number;
  sendBlocker?: string | null;
  decisionBlocker?: string | null;
  notificationIncomplete?: boolean;
  onConfirmPrice: (amount: number) => Promise<unknown>;
  onDecision: (approved: boolean, reason: string) => Promise<unknown>;
  onSendRequest: () => Promise<unknown>;
  onBypass: (reason: string) => Promise<unknown>;
  onRetry: () => Promise<unknown>;
  children?: ReactNode;
};

/** All pending commercial actions happen here; none are navigation buttons. */
export function RepairNextAction(p: RepairNextActionProps) {
  const [amount, setAmount] = useState("");
  const [basis, setBasis] = useState("");
  const [bypassOpen, setBypassOpen] = useState(false);
  const [bypassReason, setBypassReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const inFlight = useRef(false);
  const priceStage = p.status === "awaiting_commercial_review";
  const declined = p.status === "customer_declined";
  const awaitingCustomer = p.status === "awaiting_customer_confirmation";
  const disabled = busy || Boolean(saved);
  const run = async (action: () => Promise<unknown>, message: string) => {
    if (inFlight.current || saved) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
      setSaved(message);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "The action could not be saved. Please retry."
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const priceValid =
    amount.trim() !== "" &&
    Number.isFinite(Number(amount)) &&
    Number(amount) > 0;
  const title = declined
    ? "Customer did not approve — arrange the next step"
    : priceStage
      ? "Confirm the customer selling price"
      : p.canDecide
        ? "Record the customer's decision"
        : `Waiting for ${p.picName}`;
  return (
    <section
      data-testid="ro-next-action"
      className="rounded-2xl bg-[#0f1c3f] p-5 text-white shadow-sm sm:p-6"
    >
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-300">
        Next action
      </p>
      <h2 className="mt-2 text-xl font-semibold sm:text-2xl">{title}</h2>
      <p className="mt-2 text-sm text-slate-200">
        Responsible:{" "}
        <strong className="text-white">
          {priceStage
            ? p.canSetPrice
              ? `${p.actorName} (Admin / Finance)`
              : "Admin / Finance"
            : `${p.picName} (PIC)`}
        </strong>
      </p>
      <dl className="mt-4 grid gap-3 rounded-xl bg-white/10 p-4 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-slate-300">Technician guide cost</dt>
          <dd className="mt-1 font-semibold">
            {p.guideCost ? `S$${p.guideCost}` : "Not available to this viewer"}
          </dd>
        </div>
        <div>
          <dt className="text-slate-300">Customer selling price</dt>
          <dd className="mt-1 font-semibold">
            {p.confirmedPrice
              ? `S$${p.confirmedPrice} · v${p.proposalVersion}`
              : p.proposalVersion > 0 && !priceStage
                ? "Confirmed · restricted view"
                : "Not confirmed"}
          </dd>
        </div>
        <div>
          <dt className="text-slate-300">Assigned technician</dt>
          <dd className="mt-1 font-semibold">
            {p.technicianName || "Unassigned"}
          </dd>
        </div>
      </dl>
      {saved ? (
        <p
          role="status"
          className="mt-4 rounded-lg bg-emerald-950 p-3 text-emerald-100"
        >
          {saved}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="mt-4 rounded-lg border border-red-300 bg-red-950 p-3 text-red-100"
        >
          {error}
        </p>
      ) : null}
      {priceStage ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-200">
            The guide cost is not the customer price. After confirmation,{" "}
            {p.picName} records or requests the customer's decision.
          </p>
          {p.canSetPrice ? (
            <form
              className="space-y-3"
              onSubmit={e => {
                e.preventDefault();
                if (priceValid)
                  void run(
                    () => p.onConfirmPrice(Number(amount)),
                    "Selling price saved. The PIC can now record or request the customer's decision."
                  );
              }}
            >
              <label
                className="block text-sm font-medium"
                htmlFor="next-selling-price"
              >
                Customer selling price (S$)
              </label>
              <div className="flex max-w-md flex-wrap gap-2">
                <Input
                  id="next-selling-price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="min-w-40 flex-1 bg-white text-slate-900"
                  value={amount}
                  disabled={disabled}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Enter the amount to charge"
                />
                {p.guideCost && Number(p.guideCost) > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-transparent text-white"
                    disabled={disabled}
                    onClick={() => setAmount(p.guideCost!)}
                  >
                    Use guide cost
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-slate-300">
                This confirms the price only. It does not approve repair or
                contact the customer.
              </p>
              <Button
                type="submit"
                className="bg-white font-semibold text-slate-900 hover:bg-slate-100"
                disabled={disabled || !priceValid}
              >
                {busy ? "Saving…" : "CONFIRM SELLING PRICE"}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-amber-200">
              Admin / Finance must confirm the selling price. You cannot set it
              with your current role.
            </p>
          )}
        </div>
      ) : declined ? (
        <p className="mt-4 text-sm text-amber-200">
          Repair is not approved. {p.picName} must follow up with the customer.
          This decision does not delete or close the repair order.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {p.canDecide ? (
            <>
              <p className="text-sm text-slate-200">
                Record the customer's actual decision on the confirmed price.
                Your identity, the price, version and reason are audited.
              </p>
              <label
                htmlFor="next-decision-basis"
                className="block text-sm font-medium"
              >
                Decision basis (required)
              </label>
              <Textarea
                id="next-decision-basis"
                className="bg-white text-slate-900"
                disabled={disabled}
                maxLength={2000}
                value={basis}
                onChange={e => setBasis(e.target.value)}
                placeholder="How the customer agreed, or their reason for not approving"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  className="bg-emerald-600 font-semibold text-white hover:bg-emerald-700"
                  disabled={disabled || !basis.trim()}
                  onClick={() =>
                    void run(
                      () => p.onDecision(true, basis.trim()),
                      "APPROVED recorded. Repair is authorized."
                    )
                  }
                >
                  APPROVED
                </Button>
                <Button
                  className="bg-red-600 font-semibold text-white hover:bg-red-700"
                  disabled={disabled || !basis.trim()}
                  onClick={() =>
                    void run(
                      () => p.onDecision(false, basis.trim()),
                      "NOT APPROVED recorded. Repair is blocked; PIC follow-up is required."
                    )
                  }
                >
                  NOT APPROVED
                </Button>
                {basis ? (
                  <Button
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => {
                      setBasis("");
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-slate-300">
                NOT APPROVED records customer refusal and stops repair work. It
                does not delete or close the order.
              </p>
            </>
          ) : (
            <p className="text-sm text-amber-200">
              {p.decisionBlocker ||
                `Only ${p.picName}, the assigned active PIC, can record the customer's decision.`}
            </p>
          )}
          {awaitingCustomer ? (
            <p className="text-sm text-slate-200">
              The approval request has been sent. The customer can respond
              through their link, or the PIC can record their decision above.
            </p>
          ) : p.canDecide ? (
            <div className="border-t border-white/20 pt-3">
              <Button
                variant="outline"
                className="bg-transparent text-white"
                disabled={disabled || Boolean(p.sendBlocker)}
                onClick={() =>
                  void run(
                    p.onSendRequest,
                    "Approval request sent. Waiting for the customer's response."
                  )
                }
              >
                Send customer approval request
              </Button>
              {p.sendBlocker ? (
                <p className="mt-2 text-sm text-amber-200">
                  Cannot send: {p.sendBlocker}
                </p>
              ) : null}
            </div>
          ) : p.sendBlocker ? (
            <p className="text-sm text-amber-200">
              Customer request blocker: {p.sendBlocker}
            </p>
          ) : null}
        </div>
      )}
      {!declined && p.canBypass ? (
        <div className="mt-5 border-t border-white/20 pt-4">
          {!bypassOpen ? (
            <Button
              variant="outline"
              className="bg-transparent text-amber-100"
              disabled={disabled}
              onClick={() => setBypassOpen(true)}
            >
              Start repair without approval
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-amber-200">
                This starts a work timer for you without recording customer
                approval. The customer decision and billing requirements remain
                outstanding. A price revision or refusal will stop this session.
              </p>
              <label
                htmlFor="next-bypass-reason"
                className="block text-sm font-medium"
              >
                Reason for starting without approval (required)
              </label>
              <Textarea
                id="next-bypass-reason"
                className="bg-white text-slate-900"
                disabled={disabled}
                maxLength={2000}
                value={bypassReason}
                onChange={e => setBypassReason(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  className="bg-amber-300 text-slate-950 hover:bg-amber-200"
                  disabled={disabled || !bypassReason.trim()}
                  onClick={() =>
                    void run(
                      () => p.onBypass(bypassReason.trim()),
                      "Work started without approval. The exception is audited; customer approval remains outstanding."
                    )
                  }
                >
                  Confirm start without approval
                </Button>
                <Button
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => {
                    setBypassOpen(false);
                    setBypassReason("");
                  }}
                >
                  Cancel bypass
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
      <div className="mt-4">{p.children}</div>
      {p.notificationIncomplete ? (
        <div className="mt-4 text-sm text-amber-200">
          <p>
            The decision is saved. An internal notification is still pending;
            check assignment and delivery.
          </p>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={async () => {
              if (inFlight.current) return;
              inFlight.current = true;
              setBusy(true);
              try {
                await p.onRetry();
              } catch (e) {
                setError(e instanceof Error ? e.message : "Retry failed");
              } finally {
                inFlight.current = false;
                setBusy(false);
              }
            }}
          >
            Retry internal notification
          </Button>
        </div>
      ) : null}
    </section>
  );
}
